import React, { useState } from 'react'
import useIsResponsive from '@/hooks/useIsResponsive'
import { Box } from '@mui/material'
import { EmtTypography, EmtModal, EmtButton } from 'emt-components-v2'

import AnimalForm, { AnimalData } from '@/components/PetSitter/AddAnimal/AnimalForm'
import AnimalBox from '@/components/PetSitter/AnimalBox'
import AddBox from '@/components/PetSitter/AddBox'
import { useSignUpCallRequestStore } from '@/store/signUpCallRequestStore'

interface EditPetsModalProps {
  open: boolean
  setOpen: (open: boolean) => void
}

const EditPetsModal: React.FC<EditPetsModalProps> = ({ open, setOpen }) => {
  const [isMobile] = useIsResponsive()
  const pets = useSignUpCallRequestStore((s) => s.animals)
  const addAnimal = useSignUpCallRequestStore((s) => s.addAnimal)
  const updateAnimal = useSignUpCallRequestStore((s) => s.updateAnimal)
  const removeAnimal = useSignUpCallRequestStore((s) => s.removeAnimal)
  const [showForm, setShowForm] = useState(false)
  const [selectedPet, setSelectedPet] = useState<AnimalData | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleFormSubmit = (values: AnimalData) => {
    setIsLoading(true)
    if (selectedPet) {
      updateAnimal({ ...values, uuid: selectedPet.uuid })
    } else {
      const uuid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      addAnimal({ ...values, uuid })
    }
    setShowForm(false)
    setSelectedPet(null)
    setIsLoading(false)
  }

  const editAnimal = (uuid: string) => {
    const petToEdit = pets.find((p) => p.uuid === uuid)
    if (!petToEdit) return
    setSelectedPet(petToEdit)
    setShowForm(true)
  }

  const deleteAnimal = (uuid: string) => {
    if (pets.length <= 1) return // Prevent deletion if only one animal
    removeAnimal(uuid)
    setShowForm(false)
    setSelectedPet(null)
  }

  const handleAddPet = () => {
    setSelectedPet(null)
    setShowForm(true)
  }

  return (
    <EmtModal
      open={open}
      onClose={(event: React.MouseEvent) => {
        event.stopPropagation()
        setOpen(false)
      }}
      sx={{
        '& .inner-box': { width: '800px', maxWidth: '90vw' },
        '& .children-box': { padding: isMobile ? '26px' : '66px' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
        }}
      >
        <EmtTypography variant="h4" fontWeight={800}>
          Modifier mes animaux
        </EmtTypography>
        {pets.map((pet) =>
          showForm && selectedPet && pet.uuid === selectedPet.uuid ? (
            <Box key={pet.uuid} sx={{ backgroundColor: '#fff', borderRadius: '16px' }}>
              <AnimalForm
                initialValues={pet}
                onSubmit={handleFormSubmit}
                onCancel={() => {
                  setShowForm(false)
                  setSelectedPet(null)
                }}
                isLoading={isLoading}
                color="purple"
                canCancel={pets.length > 0}
                variant="signup"
                defaultImages
                defaultDescription={
                  "La description de cet animal n'a pas été donnée par son propriétaire. Si vous en avez besoin, vous pouvez l'encourager à vous donner une description via la messagerie."
                }
              />
            </Box>
          ) : (
            <AnimalBox
              key={pet.uuid}
              data={pet}
              color="purple"
              edit={editAnimal}
              // Hide or disable delete if only one animal
              delete={pets.length > 1 ? deleteAnimal : undefined}
            />
          ),
        )}
        {(!showForm || (selectedPet && !pets.some((pet) => pet.uuid === selectedPet.uuid))) && (
          <AddBox handleAddPet={handleAddPet} />
        )}
        {showForm && !selectedPet && (
          <Box sx={{ backgroundColor: '#fff', p: '25px', borderRadius: '16px', mt: 2 }}>
            <AnimalForm
              key="new"
              onSubmit={handleFormSubmit}
              onCancel={() => {
                setShowForm(false)
                setSelectedPet(null)
              }}
              isLoading={isLoading}
              color="purple"
              canCancel={pets.length > 0}
              variant="signup"
              defaultImages
              defaultDescription={
                "La description de cet animal n'a pas été donnée par son propriétaire. Si vous en avez besoin, vous pouvez l'encourager à vous donner une description via la messagerie."
              }
            />
          </Box>
        )}
        <Box sx={{ width: '100%', display: 'flex', gap: '10px', justifyContent: 'center', mt: 2 }}>
          <EmtButton fullWidth variant="contained" size="medium" color="purple" onClick={() => setOpen(false)}>
            Continuer
          </EmtButton>
        </Box>
      </Box>
    </EmtModal>
  )
}

export default EditPetsModal
